import { useState, useRef, useEffect, memo } from "react"
import Loader from "../../ui/loader"
import { Button } from "../../ui/button"
import { SvgCheckbox, SvgCheckboxOff } from "../../icons"
import { useLocation } from "react-router-dom"
import { useToast } from "../../../hooks/useToast"

type PaymentSource = "paypal" | "card" | "apple_pay" | "google-pay"

interface CardFieldsProps {
  isCheckedRef: React.MutableRefObject<boolean>
  createOrder?: (data: any, source: PaymentSource) => Promise<any>
  onApprove: (data: any, source: PaymentSource) => Promise<any>
  createVaultSetupToken?: (data: any, source: PaymentSource) => Promise<any>
  isChecked: boolean
  setIsChecked: (checked: boolean) => void
  isTermsAgreed: boolean
  setIsTermsAgreed: React.Dispatch<React.SetStateAction<boolean>>
  redirectUrl?: string
  selectedPlanFromChild?: any
  isSubx: boolean
  activeService: any
  setShowModal?: React.Dispatch<React.SetStateAction<boolean>>
  payNow?: boolean
  payInFull?: boolean
  onCancel?: () => void
}

interface CardFieldInstance {
  submit: () => Promise<void>
  getState: () => Promise<any>
  isEligible: () => boolean
  NameField: (opts: any) => any
  NumberField: (opts: any) => any
  CVVField: (opts: any) => any
  ExpiryField: (opts: any) => any
}

const PayPalCardFields: React.FC<CardFieldsProps> = ({
  isCheckedRef,
  onApprove,
  createOrder,
  isChecked,
  setIsChecked,
  isSubx,
  activeService,
  createVaultSetupToken,
  setShowModal,
  payNow,
  payInFull,
  onCancel,
}) => {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [cardFieldInstance, setCardFieldInstance] =
    useState<CardFieldInstance | null>(null)

  const checkboxRef = useRef<HTMLInputElement | null>(null)
  const location = useLocation()
  const subxCheckout = !location.pathname.includes("account-settings")

  const isSaveCardLocked =
    (activeService?.payment_term === "flexible" && !payInFull) ||
    activeService?.service_details?.type === "recurring" ||
    activeService?.service_details?.billing?.unit === "installment"

  const handleChange = (event: any) => {
    if (isSaveCardLocked) return

    setIsChecked(event.target.checked)
    isCheckedRef.current = event.target.checked
  }

  useEffect(() => {
    if (isSaveCardLocked) {
      setIsChecked(true)
      isCheckedRef.current = true
    }
  }, [
    activeService?.payment_term,
    activeService?.service_details?.type,
    activeService?.service_details?.billing?.unit,
    payInFull,
  ])

  useEffect(() => {
    isCheckedRef.current = isChecked
  }, [isChecked])

  useEffect(() => {
    if (!window.paypal) return

    const cardField = window.paypal.CardFields({
      ...(createOrder
        ? { createOrder: (data: any) => createOrder?.(data, "card") }
        : {
            createVaultSetupToken: (data: any) =>
              createVaultSetupToken?.(data, "card"),
          }),
      onApprove: (data: any) => onApprove(data, "card"),
    })

    if (cardField.isEligible()) {
      cardField
        .NameField({
          required: true,
          placeholder: "Cardholder Name",
        })
        .render("#card-name-field-container")
      cardField.NumberField().render("#card-number-field-container")
      cardField.CVVField().render("#card-cvv-field-container")
      cardField.ExpiryField().render("#card-expiry-field-container")
      setCardFieldInstance(cardField)
    }
  }, [])

  const handleSubmit = () => {
    if (!cardFieldInstance) return

    cardFieldInstance.getState().then((data: any) => {
      if (data.fields.cardNameField.isEmpty) {
        toast.error("Cardholder name is required!")
        setLoading(false)
        return
      }

      setLoading(true)
      cardFieldInstance.submit().finally(() => setLoading(false))
    })
  }

  return (
    <>
      <div id="checkout-form">
        <div id="card-name-field-container"></div>
        <div id="card-number-field-container"></div>
        <div id="card-expiry-field-container"></div>
        <div id="card-cvv-field-container"></div>
      </div>

      {!isSubx && subxCheckout && (
        <div className="flex w-[95%] mb-2 mt-2">
          <input
            id="checkbox-input"
            ref={checkboxRef}
            onChange={handleChange}
            checked={isChecked}
            type="checkbox"
            className="hidden"
          />
          <label
            htmlFor={isSaveCardLocked ? undefined : "checkbox-input"}
            className={`self-start mr-4 flex items-center ${
              isSaveCardLocked
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer"
            }`}
          >
            {isChecked ? <SvgCheckbox /> : <SvgCheckboxOff fill="#CCCCCC" />}
          </label>
          <div className="text-graySlate text-base">
            <p>Save card for future payments</p>
          </div>
        </div>
      )}

      <div className="flex w-[95%] gap-3 ml-2">
        <Button
          className="flex-1 text-md"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <Loader />
          ) : subxCheckout || payNow ? (
            "Pay Now"
          ) : (
            "Save Now"
          )}
        </Button>

        {!subxCheckout && onCancel && (
          <Button
            className="flex-1 text-md"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
      </div>
    </>
  )
}

export default memo(PayPalCardFields)
